import React, { useEffect } from 'react';
import {
    Box, TextField, Button, Typography, Stack,
    FormControl, InputLabel, Select, MenuItem,
    Grid, Paper, Divider, Chip, alpha, useTheme, useMediaQuery,
} from '@mui/material';
import { Formik } from 'formik';
import * as Yup from 'yup';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import { getUserName } from 'Service/Service';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'app/store';
import { fetchAgentsAsync } from '../app/services/agents/agentSlice';
import { BASE_URL } from 'config/constant';
import toast from 'react-hot-toast';
import axios from 'axios';
import AgentSearch from 'common/AgentSearch';

const validationSchema = Yup.object().shape({
    name: Yup.string().required('Name is required'),
    mainAadhaar: Yup.string()
        .matches(/^\d{12}$/, 'Must be exactly 12 digits')
        .required('Main Aadhaar is required'),
    familyAadhaar: Yup.string()
        .matches(/^\d{12}$/, 'Must be exactly 12 digits')
        .required('Family Aadhaar is required'),
    mobile: Yup.string()
        .matches(/^\d{10}$/, 'Must be exactly 10 digits')
        .required('Mobile number is required'),
    mainAgent: Yup.string().required('Main agent is required'),
});

const initialValues = {
    name: '',
    mainAadhaar: '',
    familyAadhaar: '',
    mobile: '',
    mainAgent: '',
    subAgent: '',
    registeredAgencyName: 'JAMAN HP GAS 2021',
    consumerNo: '',
    remarks: '',
};

const SectionTitle = ({ children }: { children: React.ReactNode }) => (
    <Typography
        variant="subtitle2"
        color="text.secondary"
        sx={{ fontWeight: 600, letterSpacing: 0.5, textTransform: 'uppercase', fontSize: '0.7rem', mb: 2 }}
    >
        {children}
    </Typography>
);

const ConsumerRegistration: React.FC = () => {
    const dispatch = useDispatch();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const agents = useSelector((state: RootState) => state.agents.agents);
    const status = useSelector((state: RootState) => state.agents.status);

    useEffect(() => {
        if (status === 'idle') {
            //@ts-ignore
            dispatch(fetchAgentsAsync());
        }
    }, [status, dispatch]);

    const handleSubmit = async (values: typeof initialValues, { setSubmitting, resetForm }: any) => {
        try {
            const result = await axios.post(BASE_URL + 'customer/add', {
                name: values.name,
                mainAadhaar: values.mainAadhaar,
                familyAadhaar: values.familyAadhaar,
                mainAgent: values.mainAgent,
                mobile: values.mobile,
                consumerNo: '',
                subAgent: values.subAgent,
                registeredAgencyName: values.registeredAgencyName,
                remarks: values.remarks,
                addedBy: getUserName(),
            });
            if (result.data.data && result.data != null) {
                toast.success('Consumer added successfully');
                resetForm();
            }
        } catch (error) {
            //@ts-ignore
            toast.error(error.response.data.message);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
            {/* Page Header */}
            <Box
                sx={{
                    background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 60%, ${theme.palette.primary.light} 100%)`,
                    px: { xs: 2, sm: 4 },
                    py: { xs: 2.5, sm: 3.5 },
                    display: 'flex',
                    alignItems: 'center',
                    borderRadius: 1,
                    gap: 2,
                    boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.4)}`,
                }}
            >
                <Box
                    sx={{
                        bgcolor: alpha('#fff', 0.15),
                        borderRadius: 2,
                        p: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <HowToRegIcon sx={{ color: '#fff', fontSize: { xs: 28, sm: 36 } }} />
                </Box>
                <Box>
                    <Typography
                        variant={isMobile ? 'h5' : 'h4'}
                        fontWeight={700}
                        sx={{ color: '#fff', lineHeight: 1.2 }}
                    >
                        Consumer Registration
                    </Typography>
                    <Typography variant="body2" sx={{ color: alpha('#fff', 0.8), mt: 0.3 }}>
                        Register a new LPG consumer under Jaman HP Gas
                    </Typography>
                </Box>
            </Box>

            {/* Form */}
            <Box sx={{ px: { xs: 1, sm: 3 }, pt: 2, pb: 4 }}>
                <Formik
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                >
                    {({ values, errors, touched, handleChange, handleBlur, handleSubmit, isSubmitting, setFieldValue }) => (
                        <form onSubmit={handleSubmit}>
                            <Box
                                sx={{
                                    bgcolor: 'background.paper',
                                    borderRadius: 3,
                                    boxShadow: `0 2px 12px ${alpha(theme.palette.grey[500], 0.12)}`,
                                    overflow: 'hidden',
                                }}
                            >
                                {/* Form header strip */}
                                <Box
                                    sx={{
                                        px: { xs: 1.5, sm: 3 },
                                        py: 2,
                                        bgcolor: alpha(theme.palette.primary.main, 0.04),
                                        borderBottom: `1px solid ${theme.palette.divider}`,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1.5,
                                    }}
                                >
                                    <PersonAddIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                                    <Typography variant="subtitle1" fontWeight={700} color="primary.main">
                                        New Consumer Details
                                    </Typography>
                                    <Chip
                                        label="Required fields marked *"
                                        size="small"
                                        sx={{
                                            ml: 'auto',
                                            height: 20,
                                            fontSize: '0.6rem',
                                            bgcolor: alpha(theme.palette.grey[500], 0.1),
                                            color: 'text.secondary',
                                        }}
                                    />
                                </Box>

                                <Box sx={{ px: { xs: 1.5, sm: 3 }, py: 3 }}>
                                    {/* Personal Info */}
                                    <SectionTitle>Personal Information</SectionTitle>
                                    <Grid container spacing={2} mb={3}>
                                        <Grid item xs={12}>
                                            <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={0.5}>
                                                Consumer Name *
                                            </Typography>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                name="name"
                                                placeholder="Enter full name"
                                                value={values.name}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                error={touched.name && Boolean(errors.name)}
                                                helperText={touched.name && errors.name}
                                            />
                                        </Grid>

                                        <Grid item xs={12} sm={6} md={4}>
                                            <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={0.5}>
                                                Mobile Number *
                                            </Typography>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                name="mobile"
                                                placeholder="10-digit mobile"
                                                value={values.mobile}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                error={touched.mobile && Boolean(errors.mobile)}
                                                helperText={touched.mobile && errors.mobile}
                                                InputProps={{
                                                    startAdornment: <PhoneAndroidIcon sx={{ fontSize: 16, color: 'text.disabled', mr: 0.5 }} />,
                                                }}
                                            />
                                        </Grid>

                                        <Grid item xs={12} sm={6} md={4}>
                                            <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={0.5}>
                                                Main Aadhaar *
                                            </Typography>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                name="mainAadhaar"
                                                placeholder="12-digit Aadhaar"
                                                value={values.mainAadhaar}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                error={touched.mainAadhaar && Boolean(errors.mainAadhaar)}
                                                helperText={touched.mainAadhaar && errors.mainAadhaar}
                                                InputProps={{
                                                    startAdornment: <FingerprintIcon sx={{ fontSize: 16, color: 'text.disabled', mr: 0.5 }} />,
                                                }}
                                            />
                                        </Grid>

                                        <Grid item xs={12} sm={6} md={4}>
                                            <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={0.5}>
                                                Family Aadhaar *
                                            </Typography>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                name="familyAadhaar"
                                                placeholder="12-digit Aadhaar"
                                                value={values.familyAadhaar}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                error={touched.familyAadhaar && Boolean(errors.familyAadhaar)}
                                                helperText={touched.familyAadhaar && errors.familyAadhaar}
                                                InputProps={{
                                                    startAdornment: <FingerprintIcon sx={{ fontSize: 16, color: 'text.disabled', mr: 0.5 }} />,
                                                }}
                                            />
                                        </Grid>
                                    </Grid>

                                    <Divider sx={{ mb: 3 }} />

                                    {/* Agent Info */}
                                    <SectionTitle>Agent & Agency</SectionTitle>
                                    <Grid container spacing={2} mb={3}>
                                        <Grid item xs={12} sm={6} md={4}>
                                            <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={0.5}>
                                                Main Agent *
                                            </Typography>
                                            <FormControl fullWidth>
                                                <AgentSearch
                                                    onSelectAgent={(agent: any) => {
                                                        setFieldValue('mainAgent', agent.name);
                                                    }}
                                                    list={agents.map((agent) => ({
                                                        label: agent.name,
                                                        value: agent.name,
                                                        ...agent,
                                                    }))}
                                                />
                                            </FormControl>
                                            {touched.mainAgent && errors.mainAgent && (
                                                <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                                                    {errors.mainAgent}
                                                </Typography>
                                            )}
                                        </Grid>

                                        <Grid item xs={12} sm={6} md={4}>
                                            <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={0.5}>
                                                Sub Agent
                                            </Typography>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                name="subAgent"
                                                placeholder="Optional sub agent"
                                                value={values.subAgent}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                            />
                                        </Grid>

                                        <Grid item xs={12} sm={6} md={4}>
                                            <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={0.5}>
                                                Registered Agency
                                            </Typography>
                                            <FormControl fullWidth size="small">
                                                <Select
                                                    name="registeredAgencyName"
                                                    value={values.registeredAgencyName}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                >
                                                    <MenuItem value="JAMAN HP GAS 2021">JAMAN HP GAS 2021</MenuItem>
                                                    <MenuItem value="GOURIPUR HP GAS PSV 2021">GOURIPUR HP GAS PSV 2021</MenuItem>
                                                    <MenuItem value="JAMAN HP GAS CLEAR KYC 2019">JAMAN HP GAS CLEAR KYC 2019</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                    </Grid>

                                    <Divider sx={{ mb: 3 }} />

                                    {/* Remarks */}
                                    <SectionTitle>Additional Notes</SectionTitle>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12}>
                                            <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={0.5}>
                                                Remarks
                                            </Typography>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                multiline
                                                rows={3}
                                                name="remarks"
                                                placeholder="Any additional notes about this consumer..."
                                                value={values.remarks}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                            />
                                        </Grid>
                                    </Grid>

                                    {/* Submit */}
                                    <Divider sx={{ mt: 3, mb: 3 }} />
                                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="flex-end">
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            size="large"
                                            type="submit"
                                            disabled={isSubmitting || !values.mainAgent}
                                            startIcon={<HowToRegIcon />}
                                            sx={{
                                                borderRadius: 2,
                                                fontWeight: 700,
                                                textTransform: 'none',
                                                px: 4,
                                                boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.4)}`,
                                                '&:hover': {
                                                    boxShadow: `0 6px 18px ${alpha(theme.palette.primary.main, 0.55)}`,
                                                },
                                            }}
                                        >
                                            {isSubmitting ? 'Registering...' : 'Register Consumer'}
                                        </Button>
                                    </Stack>
                                </Box>
                            </Box>
                        </form>
                    )}
                </Formik>
            </Box>
        </Box>
    );
};

export default ConsumerRegistration;
